import React, { useState, memo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ReplyForm,
  EditForm,
  ReportModal,
  CommentActions,
  CommentLikeButton,
  MentionText,
} from "./CommentForms";
import { CommentSkeleton, CommentErrorBoundary } from "./CommentSkeleton";
import {
  useDiscussionApi,
  COMMENT_CONSTANTS,
  commentUtils,
} from "../../hooks/useDiscussionApi";

const Comment = memo(
  ({
    comment,
    user,
    refreshThread,
    expandedComments,
    setExpandedComments,
    depth = 0,
    vehicleId,
    isHighlighted,
  }) => {
    const { t } = useTranslation();
    // State management using reducers for related state
    const [replyState, setReplyState] = useState({
      show: false,
      text: "",
      error: "",
    });

    const [editState, setEditState] = useState({
      isEditing: false,
      text: comment.comment || "",
    });

    const [reportState, setReportState] = useState({
      showModal: false,
      reason: "",
    });

    const [threadState, setThreadState] = useState({
      isCollapsed: depth >= COMMENT_CONSTANTS.MAX_DEPTH,
      replies: comment.replies || [],
      isLoading: false,
      hasMore: comment.hasMoreReplies || false,
      currentPage: COMMENT_CONSTANTS.INITIAL_PAGE,
    });

    const api = useDiscussionApi();

    // Check if current user has liked this comment
    const isLikedByUser =
      comment.likes?.some((like) => like.user === user?._id) || false;

    const handleExpandClick = async () => {
      if (!threadState.isCollapsed) {
        setThreadState((prev) => ({ ...prev, isCollapsed: true }));
        setExpandedComments(
          expandedComments.filter((id) => id !== comment._id)
        );
        return;
      }

      setThreadState((prev) => ({
        ...prev,
        isCollapsed: false,
        isLoading: true,
      }));
      setExpandedComments([...expandedComments, comment._id]);

      try {
        const data = await api.fetchReplies(comment._id, 1);
        setThreadState((prev) => ({
          ...prev,
          replies: data.replies,
          hasMore: data.hasMore,
          currentPage: data.nextPage,
          isLoading: false,
        }));
      } catch (err) {
        setThreadState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    const handleLoadMore = async () => {
      setThreadState((prev) => ({ ...prev, isLoading: true }));
      try {
        const data = await api.fetchReplies(
          comment._id,
          threadState.currentPage
        );
        setThreadState((prev) => ({
          ...prev,
          replies: [...prev.replies, ...data.replies],
          hasMore: data.hasMore,
          currentPage: data.nextPage,
          isLoading: false,
        }));
      } catch (err) {
        setThreadState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    const handleReplySubmit = async (e) => {
      e.preventDefault();
      if (!replyState.text.trim()) return;

      try {
        const newReply = await api.submitReply(comment._id, replyState.text);
        if (typeof newReply.user === "string") {
          newReply.user = {
            _id: newReply.user,
            username: newReply.username,
          };
        }
        setThreadState((prev) => ({
          ...prev,
          replies: [...prev.replies, newReply],
          isCollapsed: false,
        }));
        comment.replyCount = (comment.replyCount || 0) + 1;
        setReplyState({ show: false, text: "", error: "" });
      } catch (err) {
        setReplyState((prev) => ({ ...prev, error: err.message }));
      }
    };

    const handleEdit = async () => {
      try {
        const updated = await api.editComment(comment._id, editState.text);
        comment.comment = updated.comment;
        comment.lastEditedAt = updated.lastEditedAt;
        setEditState({ isEditing: false, text: updated.comment });
        refreshThread();
      } catch (err) {
        alert("Failed to edit comment");
      }
    };

    const handleDelete = async () => {
      if (!window.confirm(t("actions.confirm"))) return;

      try {
        await api.deleteComment(comment._id);
        comment.isDeleted = true;
        setThreadState((prev) => ({ ...prev }));
      } catch (err) {
        alert(t("common.error"));
      }
    };

    const handleReport = async (e) => {
      e.preventDefault();
      try {
        await api.reportComment(comment._id, reportState.reason);
        setReportState({ showModal: false, reason: "" });
        alert("Comment reported successfully");
      } catch (err) {
        alert("Failed to report comment");
      }
    };

    const handleLike = async (commentId) => {
      try {
        return await api.likeComment(commentId);
      } catch (err) {
        throw err;
      }
    };

    // Don't render if comment is hidden and not approved
    if (comment.isHidden && comment.moderationStatus !== "approved") {
      return (
        <div className="text-gray-500 italic p-2">
          {t("discussion.hidden_moderation")}
        </div>
      );
    }

    // Show deleted message if comment is soft deleted
    if (comment.isDeleted) {
      return (
        <div
          id={`comment-${comment._id}`}
          style={{
            marginLeft: depth > 0 ? "1rem" : "0",
            borderLeft: depth > 0 ? "2px solid #e5e7eb" : "4px solid #bfdbfe",
            marginBottom: "0.5rem",
          }}
        >
          <div className="text-gray-400 text-sm italic p-2">
            {t("discussion.deleted")}
          </div>
          {commentUtils.shouldShowReplies(comment) && (
            <>
              {threadState.isCollapsed ? (
                <button
                  onClick={handleExpandClick}
                  className="text-blue-500 hover:text-blue-600 text-sm p-2"
                >
                  {t(
                    comment.replyCount === 1
                      ? "discussion.show_reply"
                      : "discussion.show_replies",
                    {
                      count: comment.replyCount || threadState.replies.length,
                    }
                  )}
                </button>
              ) : (
                <div>
                  {threadState.replies.map((reply) => (
                    <CommentErrorBoundary key={reply._id}>
                      <Comment
                        comment={reply}
                        user={user}
                        refreshThread={refreshThread}
                        expandedComments={expandedComments}
                        setExpandedComments={setExpandedComments}
                        depth={depth + 1}
                        vehicleId={vehicleId}
                        isHighlighted={isHighlighted}
                      />
                    </CommentErrorBoundary>
                  ))}
                  {threadState.hasMore && (
                    <button
                      onClick={handleLoadMore}
                      className="text-blue-500 text-sm hover:text-blue-600 p-2"
                      disabled={threadState.isLoading}
                    >
                      {threadState.isLoading
                        ? "Loading..."
                        : "Show more replies"}
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      );
    }

    return (
      <div
        id={`comment-${comment._id}`}
        style={{
          marginLeft: depth > 0 ? "1rem" : "0",
          borderLeft: depth > 0 ? "2px solid #e5e7eb" : "4px solid #bfdbfe",
          marginBottom: "0.5rem",
        }}
        className={`transition-all duration-700 ${
          isHighlighted
            ? "bg-blue-100 shadow-md dark:bg-blue-900 dark:shadow-lg"
            : ""
        }`}
      >
        <div className="p-2">
          <div className="flex items-center gap-2">
            <div className="flex-grow">
              <p className="text-sm">
                <strong>
                  <Link
                    to={`/profile/${comment.user?.username}`}
                    className="hover:text-blue-600"
                  >
                    {comment.user?.username || t("discussion.anonymous")}
                  </Link>
                </strong>
                {comment.lastEditedAt && (
                  <span className="text-xs text-gray-500">
                    {" "}
                    {t("discussion.edited")}
                  </span>
                )}
                :{" "}
                {editState.isEditing ? (
                  <EditForm
                    text={editState.text}
                    setText={(text) =>
                      setEditState((prev) => ({ ...prev, text }))
                    }
                    onSave={handleEdit}
                    onCancel={() =>
                      setEditState({ isEditing: false, text: comment.comment })
                    }
                  />
                ) : (
                  <MentionText
                    text={comment.comment}
                    mentions={comment.mentions}
                  />
                )}
              </p>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                {commentUtils.formatDate(comment.createdAt)}
                <CommentLikeButton
                  commentId={comment._id}
                  initialLikesCount={comment.likesCount || 0}
                  initialLiked={isLikedByUser}
                  user={user}
                  onLike={handleLike}
                />
                <CommentActions
                  user={user}
                  isOwner={commentUtils.isCommentOwner(user, comment)}
                  onReport={() =>
                    setReportState((prev) => ({ ...prev, showModal: true }))
                  }
                  onEdit={() =>
                    setEditState((prev) => ({ ...prev, isEditing: true }))
                  }
                  onDelete={handleDelete}
                />
                {comment.replyCount > 0 && !threadState.isCollapsed && (
                  <span>
                    {t(
                      comment.replyCount === 1
                        ? "discussion.show_reply"
                        : "discussion.show_replies",
                      {
                        count: comment.replyCount,
                      }
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>

          {user && !editState.isEditing && (
            <div className="mt-0.5">
              <button
                onClick={() =>
                  setReplyState((prev) => ({ ...prev, show: !prev.show }))
                }
                className="text-blue-500 hover:text-blue-600 text-xs"
              >
                {t("discussion.reply")}
              </button>
            </div>
          )}

          {reportState.showModal && (
            <ReportModal
              onClose={() => setReportState({ showModal: false, reason: "" })}
              onSubmit={handleReport}
              reason={reportState.reason}
              setReason={(reason) =>
                setReportState((prev) => ({ ...prev, reason }))
              }
            />
          )}

          {replyState.show && (
            <ReplyForm
              onSubmit={handleReplySubmit}
              text={replyState.text}
              setText={(text) => setReplyState((prev) => ({ ...prev, text }))}
              error={replyState.error}
              autoFocus
            />
          )}
        </div>

        {threadState.isCollapsed && commentUtils.shouldShowReplies(comment) && (
          <button
            onClick={handleExpandClick}
            className="text-blue-500 hover:text-blue-600 text-sm p-2"
          >
            {t(
              comment.replyCount === 1
                ? "discussion.show_reply"
                : "discussion.show_replies",
              {
                count: comment.replyCount || threadState.replies.length,
              }
            )}
          </button>
        )}

        {!threadState.isCollapsed && threadState.replies.length > 0 && (
          <div>
            {threadState.isLoading && threadState.replies.length === 0 ? (
              <CommentSkeleton depth={depth + 1} />
            ) : (
              <>
                {threadState.replies.map((reply) => (
                  <CommentErrorBoundary key={reply._id}>
                    <Comment
                      comment={reply}
                      user={user}
                      refreshThread={refreshThread}
                      expandedComments={expandedComments}
                      setExpandedComments={setExpandedComments}
                      depth={depth + 1}
                      vehicleId={vehicleId}
                      isHighlighted={isHighlighted}
                    />
                  </CommentErrorBoundary>
                ))}
                {threadState.hasMore && (
                  <button
                    onClick={handleLoadMore}
                    className="text-blue-500 text-sm hover:text-blue-600 p-2"
                    disabled={threadState.isLoading}
                  >
                    {threadState.isLoading
                      ? t("discussion.loading")
                      : t("discussion.load_more")}
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    );
  }
);

Comment.propTypes = {
  comment: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    user: PropTypes.object,
    comment: PropTypes.string,
    createdAt: PropTypes.string,
    lastEditedAt: PropTypes.string,
    isDeleted: PropTypes.bool,
    isHidden: PropTypes.bool,
    moderationStatus: PropTypes.string,
    replyCount: PropTypes.number,
    replies: PropTypes.array,
    hasMoreReplies: PropTypes.bool,
    likes: PropTypes.array,
    likesCount: PropTypes.number,
    mentions: PropTypes.array,
  }).isRequired,
  user: PropTypes.object,
  refreshThread: PropTypes.func.isRequired,
  expandedComments: PropTypes.array.isRequired,
  setExpandedComments: PropTypes.func.isRequired,
  depth: PropTypes.number,
  vehicleId: PropTypes.string.isRequired,
  isHighlighted: PropTypes.bool,
};

export default Comment;
